/**
 * workflowContainerLineUtils.ts — Workflow 壳行 meta / status 辅助
 *
 * 链路位置：WorkflowContainerLine · WorkflowChrome · WorkflowStageContainer。
 */
import type { WorkflowContainerRuntimeNode } from '../../types/workflowContainer';
import type { WorkflowContainerStatus } from '../../types/workflowContainer';
import { subtreeHasActiveWork } from './workflowStageListUtils';

/** 方案 A：normal 模式 meta 首段为容器 scope（workflow / stage / item …）。 */
export function formatContainerScopeMetaLabel(
  scope: WorkflowContainerRuntimeNode['scope'],
): string {
  return scope;
}

function withScopeMeta(node: WorkflowContainerRuntimeNode, parts: string[]): string[] {
  return [formatContainerScopeMetaLabel(node.scope), ...parts];
}

export function mapContainerUiStatus(status: WorkflowContainerStatus): string {
  switch (status) {
    case 'completed':
      return 'done';
    case 'failed':
      return 'failed';
    case 'running':
    case 'retrying':
      return 'running';
    case 'skipped':
      return 'skipped';
    default:
      return 'pending';
  }
}

export function mapContainerStatusLabel(status: WorkflowContainerStatus): string {
  switch (status) {
    case 'completed':
      return 'done';
    case 'failed':
      return 'failed';
    case 'running':
      return 'Running…';
    case 'retrying':
      return 'Retrying…';
    case 'skipped':
      return 'skipped';
    case 'blocked':
      return 'blocked';
    case 'cancelled':
      return 'cancelled';
    default:
      return 'pending';
  }
}

export function mapWorkflowRootStatusLabel(status: WorkflowContainerStatus): string {
  switch (status) {
    case 'completed':
      return 'done';
    case 'failed':
      return 'error';
    default:
      return 'running';
  }
}

export function buildStageProgressMeta(node: WorkflowContainerRuntimeNode): string | null {
  const display = node.display;
  if (display?.progress_label) return display.progress_label;
  if (display?.progress_current != null && display?.progress_total != null) {
    return `Step ${display.progress_current}/${display.progress_total}`;
  }
  return null;
}

export function buildWorkflowRootMeta(node: WorkflowContainerRuntimeNode): string[] {
  const meta: string[] = [];
  if (node.pattern) meta.push(node.pattern);
  const display = node.display;
  if (display?.progress_current != null && display?.progress_total != null) {
    meta.push(`${display.progress_current}/${display.progress_total}`);
  }
  return withScopeMeta(node, meta);
}

/** 子树仍有 active 容器时，根 progress 不计入未 settle 的 stage（防 mid-run 误显示 3/3）。 */
export function buildEffectiveWorkflowRootMeta(
  node: WorkflowContainerRuntimeNode,
  containersById: Record<string, WorkflowContainerRuntimeNode>,
): string[] {
  const meta: string[] = [];
  if (node.pattern) meta.push(node.pattern);
  const topStages = Object.values(containersById).filter(
    (n) => n.scope === 'stage' && n.parent_container_id === node.container_id,
  );
  const total = node.display?.progress_total ?? Math.max(topStages.length, 1);
  let settled = 0;
  for (const stage of topStages) {
    if (subtreeHasActiveWork(containersById, stage.container_id)) continue;
    if (
      stage.status === 'completed'
      || stage.status === 'failed'
      || stage.status === 'skipped'
    ) {
      settled += 1;
    }
  }
  if (total > 0) meta.push(`${settled}/${total}`);
  return withScopeMeta(node, meta);
}

export function isWorkflowRootVisuallyActive(
  node: WorkflowContainerRuntimeNode,
  containersById: Record<string, WorkflowContainerRuntimeNode>,
): boolean {
  if (node.status === 'running' || node.status === 'retrying') return true;
  return subtreeHasActiveWork(containersById, node.container_id);
}

export function buildStageLineMeta(node: WorkflowContainerRuntimeNode): string[] {
  const progress = buildStageProgressMeta(node);
  return withScopeMeta(node, progress ? [progress] : []);
}

export function buildAggregateLineMeta(node: WorkflowContainerRuntimeNode): string[] {
  return withScopeMeta(node, []);
}

/** Route branch：decision_label + 可选 step progress。 */
export function buildRouteLineMeta(node: WorkflowContainerRuntimeNode): string[] {
  const meta: string[] = [];
  const decision = node.display?.decision_label;
  if (decision) meta.push(decision);
  const progress = buildStageProgressMeta(node);
  if (progress) meta.push(progress);
  return withScopeMeta(node, meta);
}

/** depth ≥4 时略减缩进，防深层 subworkflow 横向溢出（D24 §5.4 / R3）。 */
export function workflowIndentUnit(depth: number): string {
  return depth >= 4 ? '0.75rem' : '1rem';
}

function countSettledItems(items: WorkflowContainerRuntimeNode[]): {
  settled: number;
  total: number;
} {
  let settled = 0;
  for (const item of items) {
    if (item.status === 'completed' || item.status === 'failed') {
      settled += 1;
    }
  }
  return { settled, total: items.length };
}

/** Aggregate waiting (k/n)：display 优先，否则统计同级 parallel item。 */
export function buildAggregateStatusLabel(
  node: WorkflowContainerRuntimeNode,
  siblingItems: WorkflowContainerRuntimeNode[] = [],
): string {
  const display = node.display;
  let current = display?.progress_current;
  let total = display?.progress_total;

  if ((current == null || total == null) && siblingItems.length > 0) {
    const { settled, total: itemTotal } = countSettledItems(siblingItems);
    current = settled;
    total = itemTotal;
  }

  if (node.status === 'pending' || node.status === 'blocked') {
    if (current != null && total != null) {
      return `waiting (${current}/${total})`;
    }
    return 'waiting';
  }

  if (current != null && total != null && (node.status === 'running' || node.status === 'retrying')) {
    return `waiting (${current}/${total})`;
  }

  return mapContainerStatusLabel(node.status);
}

export function mapAggregateUiStatus(node: WorkflowContainerRuntimeNode): string {
  if (node.status === 'pending' || node.status === 'blocked') return 'pending';
  if (node.status === 'completed') return 'done';
  if (node.status === 'failed') return 'failed';
  if (node.status === 'running' || node.status === 'retrying') return 'running';
  return mapContainerUiStatus(node.status);
}
